import React from "react";
import Drawer from "@mui/material/Drawer";
import LoadingButton from "@mui/lab/LoadingButton";
import { toast } from "react-toastify";

import { API_update_company_pipeline } from "../../../constants";
import { postRightworkAPI } from "../requests/utilities/requests";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";


function SelectionCard({ active, title, description, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start justify-between rounded-2xl border px-4 py-3 text-left transition-colors ${
        active
          ? "border-violet-300 bg-violet-50"
          : "border-slate-200 bg-white hover:border-violet-200 hover:bg-slate-50"
      }`}
    >
      <div className="pr-3">
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="mt-1 text-xs text-slate-600">{description}</p>
      </div>
      <span
        className={`rounded-xl border px-3 py-2 text-sm font-semibold ${
          active
            ? "border-violet-200 bg-violet-100 text-violet-700"
            : "border-slate-200 bg-white text-slate-600"
        }`}
      >
        {active ? "Selected" : "Select"}
      </span>
    </button>
  );
}


function OnboardingCompanyPipelineDrawer({
  open,
  onClose,
  summary,
  onSubmitted,
}) {
  const [pipelineEnabled, setPipelineEnabled] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setPipelineEnabled(Boolean(summary?.company?.pipeline_enabled));
  }, [open, summary?.company?.pipeline_enabled]);

  const handleSubmit = async () => {
    if (!summary?.company?.id) {
      return;
    }

    setSubmitting(true);
    try {
      await postRightworkAPI(API_update_company_pipeline, {
        company_id: summary.company.id,
        pipeline_enabled: pipelineEnabled,
      });
      toast.success(
        pipelineEnabled
          ? "Company pipeline enabled."
          : "Company pipeline disabled.",
        { autoClose: 3000 }
      );
      await onSubmitted?.();
      onClose?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Unable to update company pipeline.",
        { autoClose: 4000 }
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer open={open} anchor="right" onClose={onClose}>
      <div className="flex h-full w-[560px] max-w-[100vw] flex-col bg-white">
        <div className="border-b border-slate-200">
          <DrawerHeader
            title="Initialize Company Pipeline"
            onClose={onClose}
          />
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-28">
          <div className="mt-5 rounded-2xl border border-violet-200 bg-violet-50/60 p-4">
            <p className="text-sm font-semibold text-slate-900">
              Company automation
            </p>
            <p className="mt-1 text-sm text-slate-600">
              Turn the company pipeline on to allow daily company-level automation to run, or leave it off if this company is not ready yet.
            </p>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
              Pipeline status
            </p>
            <div className="mt-4 space-y-3">
              <SelectionCard
                active={pipelineEnabled}
                title="Enabled"
                description="Daily pipeline processing is turned on for this company."
                onClick={() => setPipelineEnabled(true)}
              />
              <SelectionCard
                active={!pipelineEnabled}
                title="Disabled"
                description="Daily pipeline processing stays off until you decide to enable it."
                onClick={() => setPipelineEnabled(false)}
              />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-slate-200 bg-white px-6 py-4">
          <LoadingButton
            variant="contained"
            loading={submitting}
            onClick={handleSubmit}
            sx={{
              width: "100%",
              textTransform: "none",
              borderRadius: "12px",
              paddingY: "10px",
              backgroundColor: "#6d28d9",
              fontSize: "15px",
              "&:hover": {
                backgroundColor: "#5b21b6",
              },
            }}
          >
            Save pipeline setting
          </LoadingButton>
          <p className="mt-2 text-xs text-slate-500">
            You can change this again later from onboarding if the company&apos;s automation readiness changes.
          </p>
        </div>
      </div>
    </Drawer>
  );
}


export default OnboardingCompanyPipelineDrawer;
